/**
 * Vertical bounds for a downward "what is the surface at this column?" raycast.
 *
 * SPAWN_RAYCAST_HEIGHT is the ray's ORIGIN, not a limit, so on its own it silently breaks once terrain is
 * taller than it: the ray starts *below* the summit and a downward cast misses the mountain entirely,
 * reporting no ground where there is plenty. At 200 m that was safe for terrain topping out around 55 m,
 * but landform worlds now reach a kilometre by default and four on the top slider setting.
 *
 * So the probe is sized from the terrain that is actually loaded, keeping the constant as a floor for a
 * nearly-flat world. `far` reaches the bottom of the loaded terrain too, which matters for the ocean floor:
 * a fixed `origin * 2` stops short once the sea is hundreds of metres deep.
 */
import * as THREE from 'three';
import { SPAWN_RAYCAST_HEIGHT } from '@worldify/shared';

/** Clearance above/below the loaded terrain, in meters. */
const PROBE_MARGIN = 32;

const _union = new THREE.Box3();
const _one = new THREE.Box3();

export interface ColumnProbe {
  /** Y to start the downward ray from — above all loaded terrain. */
  originY: number;
  /** Ray length, reaching below the lowest loaded terrain (deep ocean floors included). */
  far: number;
  /** Top of the loaded terrain plus clearance, or null when nothing is loaded. */
  terrainTop: number | null;
}

const FALLBACK: ColumnProbe = {
  originY: SPAWN_RAYCAST_HEIGHT,
  far: SPAWN_RAYCAST_HEIGHT * 2,
  terrainTop: null,
};

/**
 * The sizing law itself, over a vertical extent of loaded terrain.
 *
 * Split out from the mesh path because the two probes answer different questions and must not
 * share a source: SPAWN placement reads the VOXEL FIELD (it has to agree with what the player
 * collides against, and it answers before a chunk is meshed), while the explore MARKER picks
 * against render meshes (it has to hit what is drawn, including coarse far-LOD rings). Same
 * arithmetic, two extents.
 */
export function columnProbeForExtent(minY: number, maxY: number): ColumnProbe {
  if (!Number.isFinite(minY) || !Number.isFinite(maxY) || maxY < minY) return FALLBACK;
  const terrainTop = maxY + PROBE_MARGIN;
  const originY = Math.max(SPAWN_RAYCAST_HEIGHT, terrainTop);
  return { originY, far: originY - minY + PROBE_MARGIN, terrainTop };
}

export function columnProbe(meshes: ReadonlyArray<THREE.Object3D>): ColumnProbe {
  if (meshes.length === 0) return FALLBACK;

  _union.makeEmpty();
  for (const m of meshes) {
    // A BatchedMesh's geometry is one buffer of chunk-LOCAL positions (dead ranges included), so
    // Box3.setFromObject would read a box of one chunk's size at the origin. The batch's own box is
    // the union of its live instances, each through its matrix.
    if (m instanceof THREE.BatchedMesh) {
      m.computeBoundingBox();
      if (m.boundingBox) _union.union(m.boundingBox);
    } else {
      _union.union(_one.setFromObject(m));
    }
  }
  if (_union.isEmpty()) return FALLBACK;

  return columnProbeForExtent(_union.min.y, _union.max.y);
}
